/**
 * Centralized task statistics computation.
 *
 * All task counting (open, overdue, urgent, completed, etc.) should use
 * these functions to ensure consistent definitions across the codebase.
 *
 * Key definitions:
 *  - "open"      = active | in_progress | blocked | failed (OPEN_TASK_STATUSES)
 *  - "closed"    = completed | skipped | removed (CLOSED_TASK_STATUSES)
 *  - "overdue"   = open AND isOverdue (server-computed, timezone-aware)
 *  - "urgent"    = open AND isUrgent AND NOT isOverdue (overdue takes precedence)
 *  - "counted"   = not removed, not skipped (included in completion denominator)
 */

import { Task, TaskSummary } from "../models";
import { isTaskOpen } from "./index";
import { NOT_COUNTED_TASK_STATUSES, OPEN_TASK_STATUSES } from "../constants";

export interface TaskStats {
  /** Tasks in an open/actionable state */
  openCount: number;
  /** Open tasks that are overdue (server-computed) */
  overdueCount: number;
  /** Open tasks that are urgent but not overdue */
  urgentCount: number;
  /** Tasks with status "completed" */
  completedCount: number;
  /** Tasks with status "upcoming" */
  upcomingCount: number;
  /** Total tasks included in completion tracking (excludes removed/skipped) */
  countedTotal: number;
  /** Completion percentage (0-100) */
  completionPct: number;
  /** Unique assignee IDs with open tasks */
  openAssigneeCount: number;
  /** dueOn of the earliest-due open overdue task, or null if none. */
  oldestOverdueDueOn: Date | null;
}

/** Compute all task statistics from a list of tasks. */
export function computeTaskStats(tasks: (Task | TaskSummary)[] | undefined): TaskStats {
  if (!tasks || tasks.length === 0) {
    return {
      openCount: 0,
      overdueCount: 0,
      urgentCount: 0,
      completedCount: 0,
      upcomingCount: 0,
      countedTotal: 0,
      completionPct: 0,
      openAssigneeCount: 0,
      oldestOverdueDueOn: null,
    };
  }

  let openCount = 0;
  let overdueCount = 0;
  let urgentCount = 0;
  let completedCount = 0;
  let upcomingCount = 0;
  let countedTotal = 0;
  let oldestOverdueDueOn: Date | null = null;
  const openAssigneeIds = new Set<string>();

  for (const t of tasks) {
    // Skip non-counted tasks for total
    if (!NOT_COUNTED_TASK_STATUSES.includes(t.status)) {
      countedTotal++;
    }

    if (t.status === "completed") {
      completedCount++;
    } else if (t.status === "upcoming") {
      upcomingCount++;
    }

    if (isTaskOpen(t as Task)) {
      openCount++;

      if (t.isOverdue) {
        overdueCount++;
        const due = t.dueOn ? new Date(t.dueOn) : null;
        if (due && (!oldestOverdueDueOn || due < oldestOverdueDueOn)) {
          oldestOverdueDueOn = due;
        }
      } else if (t.isUrgent) {
        urgentCount++;
      }

      if (t.assigneeId) {
        openAssigneeIds.add(t.assigneeId);
      }
    }
  }

  const completionPct = countedTotal > 0 ? Math.floor((completedCount / countedTotal) * 100) : 0;

  return {
    openCount,
    overdueCount,
    urgentCount,
    completedCount,
    upcomingCount,
    countedTotal,
    completionPct,
    openAssigneeCount: openAssigneeIds.size,
    oldestOverdueDueOn,
  };
}

/** Get open tasks sorted by priority: overdue → urgent → active. */
export function getOpenTasksSorted(tasks: TaskSummary[] | undefined, limit?: number): TaskSummary[] {
  if (!tasks) return [];

  const sorted = tasks
    .filter((t) => OPEN_TASK_STATUSES.includes(t.status))
    .sort((a, b) => {
      if (a.isOverdue && !b.isOverdue) return -1;
      if (!a.isOverdue && b.isOverdue) return 1;
      if (a.isUrgent && !b.isUrgent) return -1;
      if (!a.isUrgent && b.isUrgent) return 1;
      return 0;
    });

  return limit ? sorted.slice(0, limit) : sorted;
}

/** Get the aggregate health color for a list of tasks. */
export function getTaskHealthColor(tasks: (Task | TaskSummary)[] | undefined): "red" | "orange" | "green" {
  if (!tasks || tasks.length === 0) return "green";

  const hasOverdue = tasks.some((t) => isTaskOpen(t as Task) && t.isOverdue);
  if (hasOverdue) return "red";

  const hasOpen = tasks.some((t) => isTaskOpen(t as Task));
  if (hasOpen) return "orange";

  return "green";
}
